/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import {
    makeStyles,
} from '@material-ui/core';
import {
    arrayOf, shape, string, number, object,
} from 'prop-types';

import useClickStreamCustomEventsTracking from '../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';

import PersonalizedHomePageGridUI from '../../../GraphqlHomePage/Partials/PersonalizedHomePageGridUI/PersonalizedHomePageGridUI';
import ProductGrid from './ProductGridUI';
import PersonalizedHPCarouselBuilder from '../../../GraphqlHomePage/Partials/PersonalizedHomePageCarouselBuilder/PersonalizedHomePageCarouselBuilder';
import SimpleCarouselBuilder from '../../../EnterpriseID/Common/SimpleCarouselBuilder';

const useStyles = makeStyles((theme) => ({
    oldGridRoot: {
        width: '100%',
        margin: '0 auto 60px auto',
        maxWidth: '1380px',
        // Tablet
        [theme.breakpoints.down('md')]: {
            padding: '0 20px 0',
        },
        // Mobile
        [theme.breakpoints.down('sm')]: {
            margin: '0 0 15px 0',
            padding: '0',
        },
    },
    oldGridTitle: {
        margin: '0 0 15px 0',
        textAlign: 'center',
        width: '100%',
        '&--title': {
            margin: '0',
            textAlign: 'center',
            fontSize: '16px',
            fontWeight: '600',
        },
    },
}));

// 6810; this component is based off PersonalizedHomePageCarouselBuilder
const PLARecsBuilder = ({
    data,
    products,
}) => {
    const classes = useStyles();

    // 6810; to be removed during refactor, variable dynamicSalesforceData design is too convoluted
    const recTypeString = 'productRecommendationsSFMulti';
    const salesforceResponse = data?.productRecommendationsSFMulti?.[0] || {};

    // 6810; refactor notes
    // css and layout data from CMS and SF are destructured and recombined/renamed into new variables in different ways throughout all recommendation component rendering. recreated relevant flows here with notes about sources
    // card = personalized_product_card_reference
    // service = product_recommendations_service_v2

    // SF breakdown
    const sfPayload = salesforceResponse?.campaign?.campaignResponses?.[0]?.payload;
    const layoutDisplayType = sfPayload?.layoutDisplayType;
    const productRecTitle = sfPayload?.productRecTitle;
    const fallbackProductRecTitle = sfPayload?.fallbackProductRecTitle;
    const sfTitle = productRecTitle || fallbackProductRecTitle;
    const sfData = {
        productRecommendationsSFMulti: data[`${recTypeString}`]?.[0],
    };

    const content = salesforceResponse?.content;
    const personalizedCarouselBlock = content?.personalized_carousel || {};
    const cmsTitle = content?.title;

    // 6810; PersonalizedHomePageGridUI
    // prop: cmsData
    // only breaks out: 'min_products_to_show' and 'padding'
    // padding = from CMS, content.personalized_carousel
    // min_products_to_show = from SF, campaign...minimumProductsToShow
    const cmsData = {
        title: cmsTitle,
        min_products_to_show: sfPayload?.minimumProductsToShow || 1,
        padding: personalizedCarouselBlock?.padding,
    };
    // 6810; todo

    // 6810; this is usually assigned to a variable 'productStylingConfig' and passed as same, or productStyleConfig
    // this is the card reference if it exists, otherwise the service
    const cmsCardRef = content?.personalized_product_card_reference;
    const useCardDesign = cmsCardRef?.length > 0;
    const cmsCardRefData = useCardDesign ? cmsCardRef?.[0] : (content || {});

    // 6810; SimpleCarouselBuilder
    // name of prop = blockData
    // all data is from CMS, content.personalized_carousel
    const scbBlockObj = {
        productsToShow: content?.products_to_show,
        title: cmsTitle,
        titleFontColor: content?.title_font_color?.color,
        titleFontSize: content?.title_font_size,
        titleFontStyle: content?.title_font_style,
        titleFontFamily: content?.title_font_family,
        titleFontWeight: content?.title_font_weight,
        titleMargin: content?.title_margin,
        alignTitle: content?.align_title,
        padding: content?.padding,
    };

    // 6810; PersonalizedHomePageCarouselBuilder
    // name of prop = blockData
    // productsToShow, from CMS personalized_carousel
    // title, from SF, CMS personalized_carousel.heading, CMS title (first field of entry), hardcoded default
    // all title css, from CMS card reference, then CMS service
    // padding, from content.personalized_carousel
    const blockData = {
        productsToShow: 5 || {},
        title: cmsTitle,
        titleMargin: cmsCardRefData?.title_styling?.title_margin,
        titleFontColor: cmsCardRefData?.title_styling?.title_font_color?.color,
        titleFontSize: cmsCardRefData?.title_styling?.title_font_size,
        titleFontStyle: cmsCardRefData?.title_styling?.title_font_style,
        titleFontFamily: cmsCardRefData?.title_styling?.title_font_family,
        alignTitle: cmsCardRefData?.title_styling?.align_title,
        padding: cmsCardRefData?.title_styling?.padding,
        containerPadding: personalizedCarouselBlock?.padding,
    };

    const trackClickThrough = useClickStreamCustomEventsTracking({
        salesforceResponse,
        isFireImpression: true,
        page: { type: 'product' },
    });

    // 6810; tracking func used in grid and carousel components
    const productClickCallbackPLA = (partNumber) => {
        trackClickThrough(partNumber);
    };

    // 6810;
    // if there is reference to card
    // use grid ui, like PersonalizedHPGridUI
    // use carousel, like PersonalizedHPCarouselBuilder

    // if no card reference
    // use own grid, ProductGridUI
    // use carousel, like SimpleCarouselBuilder

    if (layoutDisplayType === 'GRID') {
        if (useCardDesign) {
            return (
                <PersonalizedHomePageGridUI
                    sfData={sfData}
                    cmsData={cmsData}
                    isNewDesign={false}
                    handleClickCallback={productClickCallbackPLA}
                    productStyleConfig={cmsCardRefData}
                    productRecStyleConfig={cmsCardRefData}
                    recommendationsType={recTypeString}
                    // isFromPLAComponent
                />
            );
        }

        return (
            <div className={classes.oldGridRoot}>
                <div className={classes.oldGridTitle}>
                    <p className={`${classes.oldGridTitle}--title`}>{sfTitle}</p>
                </div>
                <ProductGrid products={products} PLARecommendations />
            </div>
        );
    }

    if (layoutDisplayType === 'CAROUSEL') {
        if (useCardDesign) {
            return (
                <PersonalizedHPCarouselBuilder
                    products={products}
                    blockData={blockData}
                    productClickCallback={productClickCallbackPLA}
                    demandType="XS"
                    productStyleConfig={cmsCardRefData}
                    sfTitle={sfTitle}
                />
            );
        }
        return (
            <SimpleCarouselBuilder
                products={products}
                blockData={scbBlockObj}
                title={sfTitle || cmsTitle}
                productClickCallback={productClickCallbackPLA}
                eventTrackingData={{}}
            />
        );
    }

    return null;
};

PLARecsBuilder.propTypes = {
    data: object.isRequired,
    products: arrayOf(shape({
        id: string.isRequired,
        image: shape({
            path: string.isRequired,
            name: string.isRequired,
        }).isRequired,
        name: string.isRequired,
        skuPriceRange: shape({
            retail: arrayOf(shape({
                value: number.isRequired,
            })).isRequired,
            sale: arrayOf(shape({
                value: number.isRequired,
            })).isRequired,
        }).isRequired,
        seo: shape({
            url: string.isRequired,
        }).isRequired,
    })).isRequired,
};

export default PLARecsBuilder;
